import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCard, MatCardActions, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { get } from 'lodash';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CARD_SORT_REQUEST_SORTS, ECardSortOption } from '../../core/constants/card-sort.constants';
import { EDialogWidth } from '../../core/constants/ui.constants';
import { DashboardRefreshIntervalService } from '../../core/services/dashboard-refresh-interval.service';
import { UserPinService } from '../../core/services/user-pin.service';
import { IsUrlValidator } from '../../core/validators/is-url.validator';
import { TrimmedRequiredValidator } from '../../core/validators/trimmed-required.validator';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DatatableComponent } from '../../shared/components/datatable/datatable.component';
import { IDatatableColumn } from '../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { ScreenLockConfigurationComponent } from '../../shared/components/screen-lock-configuration/screen-lock-configuration.component';
import { IBaseDatatableState } from '../../state/base-datatable-store.service';
import { ELoadStatus } from '../../state/state.interface';
import { UserEffects } from '../../state/user/user.effects';
import { ICurrentUser } from '../../state/user/user.interface';
import { FormUtilitiesService } from '../../utilities/form-utilities.service';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameterFilters,
} from '../../utilities/http-utilities.service';
import { ESnackbar } from '../../utilities/snackbar.service';
import { BaseCrudPageComponent } from '../app/base-crud-page.component';
import { SlideshowService } from './slideshow.service';
import {
  IDashboardOptionForm,
  ISlideshow,
  ISlideshowCard,
  ISlideshowItem,
  TAddSlideshow,
  TFormControlSlideshow,
  TFormSlideshowComponent,
} from './slideshows.interface';
import { ISlideshowState, SlideshowsStore } from './slideshows.store';

export interface IExtendedDashboardOptionForm extends Omit<IDashboardOptionForm, 'name'> {
  name: FormControl<IDropdownOption[]>;
  type?: 'dashboard' | 'url';
}

@Component({
  imports: [
    ErrorMessagesComponent,
    ScreenLockConfigurationComponent,
    MatButton,
    MatCard,
    MatCheckbox,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslateModule,
    MatDropdown,
    MatIcon,
    AsyncPipe,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatSuffix,
    MatCardActions,
    RouterLink,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    DatatableComponent,
    MatMiniFabButton,
    MatTooltip,
  ],
  providers: [SlideshowsStore, HttpUtilitiesService],
  selector: 'app-slideshows',
  standalone: true,
  styleUrls: ['./slideshows.component.scss'],
  templateUrl: './slideshows.component.html',
})
export class SlideshowsComponent extends BaseCrudPageComponent<ISlideshow, ISlideshowState> implements OnInit {
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;
  @Input() externalSearchControl?: FormControl<string>;
  @Input() set externalSortOption(value: ECardSortOption) {
    if (this.sortOption() === value) {
      return;
    }

    this.sortOption.set(value);

    if (this.isInitialized) {
      this.getItems(this.search);
    }
  }

  public readonly sortOption: WritableSignal<ECardSortOption> = signal(ECardSortOption.name);
  private isInitialized = false;
  public readonly dashboardOptionsDatatableRows = computed(() => {
    return this.dashboardOptions()
      .map((fieldOption: IExtendedDashboardOptionForm) => ({ ...fieldOption, delete: false }))
      .sort((a, b) => this.dashboardOptionOrders().indexOf(a.id) - this.dashboardOptionOrders().indexOf(b.id));
  });
  public readonly dashboardOptionsDatatableColumns: IDatatableColumn<
    IExtendedDashboardOptionForm & {
      delete: boolean;
    },
    string
  >[] = [{ id: 'id', isSortable: false }, { id: 'name' }, { id: 'delete', isSortable: false }];

  public readonly dashboardOptionOrders: WritableSignal<string[]> = signal<string[]>([]);
  public dashboardOptions: WritableSignal<IExtendedDashboardOptionForm[]> = signal<IExtendedDashboardOptionForm[]>([]);

  public readonly workspaceDropdownConfig: IDropdownSettings = {
    hasDelayedDefaultValue: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.workspace'),
  };
  public isToolAdmin: boolean = UserEffects.getUser().isAdmin;
  public isDashboardFormInvalid = false;
  public isExtendedAccessPanel = false;
  public isExtendedScreenLockPanel = false;
  public isAutoLockConfigured = false;

  /** Same list the section renders; kept here so add/edit can map a stored duration back to a selection. */
  public readonly screenLockDurationOptions = inject(DashboardRefreshIntervalService).autoLockDurationOptions;
  public search: string | undefined;

  constructor(
    public readonly store: SlideshowsStore,
    public readonly formService: FormUtilitiesService,
    public readonly slideshowService: SlideshowService,
    private readonly userPinService: UserPinService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfSlideshows',
      multiContext: 'field.slideshows',
      nameProperty: 'name',
      singleContext: 'field.slideshow',
    });
  }

  /** Slides in the order the user arranged them; options with nothing selected are dropped. */
  public static orderDashboardItems(options: IExtendedDashboardOptionForm[], orders: string[]): ISlideshowItem[] {
    return [...options]
      .sort((a, b) => orders.indexOf(a.id) - orders.indexOf(b.id))
      .map((option): ISlideshowItem => {
        if (option.type === 'url') {
          return { type: 'url', url: option.name.value as unknown as string };
        }

        const selectedDashboard = get(option.name.value, '0') as IDropdownOption;

        return { id: selectedDashboard?.id, type: 'dashboard' };
      })
      .filter((item: ISlideshowItem) => item.id !== undefined || !!item.url);
  }

  /** A slideshow needs at least two slides, and every slide must point at something. */
  public static isSlideListInvalid(options: IExtendedDashboardOptionForm[]): boolean {
    return (
      options.length < 2 ||
      options.some(
        (option) =>
          option.name.invalid ||
          !option.name.value ||
          (option.type !== 'url' && Array.isArray(option.name.value) && option.name.value.length === 0),
      )
    );
  }

  public readonly formattedDatatableRows: Signal<ISlideshowCard[]> = computed(() =>
    this.store.data$().map((row: ISlideshow): ISlideshowCard => {
      return {
        dashboardCount: row.items.length,
        id: row.id,
        isAutoLockConfigured: row.isAutoLockConfigured,
        isAutoLockEffective: row.isAutoLockEffective,
        isEditableByCurrentUser: row.isEditableByCurrentUser,
        isOwnedBySingleUser: row.isOwnedBySingleUser,
        isPinned: row.isPinned,
        name: row.name,
        slideDurationSeconds: row.slideDurationSeconds,
        workspace: row.workspace?.name,
      };
    }),
  );

  public readonly addEditForm: FormGroup<TFormControlSlideshow> = new FormGroup<TFormControlSlideshow>({
    admins: new FormControl([] as IDropdownOption[], { validators: [Validators.required] }),
    autoLockDuration: new FormControl([] as IDropdownOption[]),
    autoLockPin: new FormControl<string | null>(null),
    isPublished: new FormControl(false, { nonNullable: true }),
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)]),
    slideDurationSeconds: new FormControl(0, [Validators.min(10), Validators.max(9999), Validators.required]),
    teams: new FormControl([] as IDropdownOption[]),
    users: new FormControl([] as IDropdownOption[]),
    workspace: new FormControl([] as IDropdownOption[], { nonNullable: true, validators: [Validators.required] }),
  });

  public readonly afterCrudSuccess$ = this.store
    .select((state: IBaseDatatableState<ISlideshow>) => state.singleCrudLoading)
    .pipe(filter((value: ELoadStatus) => value === ELoadStatus.success));

  public searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

  public ngOnInit(): void {
    if (this.externalSearchControl) {
      this.searchControl = this.externalSearchControl;
    }

    this.search = this.searchControl.value?.trim() || undefined;
    this.isInitialized = true;
    this.getItems(this.search);

    this.addEditForm.get('workspace')?.valueChanges.subscribe((value: IDropdownOption[] | null): void => {
      const workspaceId: number | undefined = get(value, '0.id');
      const adminsDropdown = this.addEditForm.get('admins');
      const usersDropdown = this.addEditForm.get('users');

      usersDropdown?.enable();
      adminsDropdown?.enable();

      if (!workspaceId) {
        usersDropdown?.disable();
        adminsDropdown?.disable();
        usersDropdown?.setValue([]);
        adminsDropdown?.setValue([]);
      }
    });
    this.subscriptions.push(
      this.afterCrudSuccess$.subscribe(() => {
        this.matDialog.closeAll();
        this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);

        this.getItems(this.search);
      }),
      this.searchControl.valueChanges
        .pipe(
          debounceTime(600),
          map((value) => value?.trim() ?? ''),
          distinctUntilChanged(),
          filter((value) => value.length === 0 || value.length >= 3),
        )
        .subscribe((value) => {
          this.search = value;
          this.getItems(value);
        }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();

    this.isAutoLockConfigured = false;
    this.addEditForm.reset({
      autoLockDuration: ScreenLockConfigurationComponent.toDurationSelection(null, this.screenLockDurationOptions),
      name: '',
      slideDurationSeconds: 60,
      workspace: [],
    });
    this.setDefaultDashboardOptions();

    this.initializeAddEditDialog();

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.small,
    });
  }

  public initializeEdit(): void {
    if (!this.selectedRows().length) {
      return;
    }

    this.addEditForm.enable();
    this.setupForEdit();
    const [item] = this.selectedRows();

    this.isAutoLockConfigured = item.isAutoLockConfigured;
    this.addEditForm.reset({
      admins: item.canEditUserIds.map((userId) => ({ id: userId, name: '...' })),
      autoLockDuration: ScreenLockConfigurationComponent.toDurationSelection(
        item.autoLockDurationSeconds,
        this.screenLockDurationOptions,
      ),
      isPublished: item.isPublished,
      name: item.name,
      slideDurationSeconds: item.slideDurationSeconds,
      teams: item.canViewTeamIds.map((teamId) => ({ id: teamId, name: '...' })),
      users: item.canViewUserIds.map((userId) => ({ id: userId, name: '...' })),
      workspace: [{ id: item.workspaceId, name: '...' }],
    });

    this.dashboardOptions.set([]);

    const authorizedItems = this.slideshowService.extractAuthorizedItems(item.items, true);

    for (const slideItem of authorizedItems) {
      if (slideItem.type === 'url' && slideItem.url) {
        this.addUrlOption(slideItem.url);
      } else if (slideItem.type === 'dashboard' && slideItem.id) {
        this.addFieldOption([{ id: slideItem.id, name: '...' }]);
      }
    }

    this.initializeAddEditDialog(item);

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.small,
    });
  }

  public onFormDialogSubmit(): void {
    const orderedDashboardItems = this.getOrderedDashboardItems();
    const isDashboardFormInvalid: boolean = this.validateDashboardForm();

    if (!this.addEditForm.valid || isDashboardFormInvalid) {
      this.isExtendedAccessPanel = this.addEditForm.controls.admins.invalid;
      this.isExtendedScreenLockPanel =
        this.addEditForm.controls.autoLockPin.invalid || this.addEditForm.controls.autoLockDuration.invalid;

      return;
    }

    if (!this.isEditDialog()) {
      this.store.createOne(
        this.formatOnePayload(this.addEditForm.value as TFormSlideshowComponent, orderedDashboardItems),
      );
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({
        dto: this.formatOnePayload(this.addEditForm.value as TFormSlideshowComponent, orderedDashboardItems),
        id: item.id,
      });
    }
  }

  public onSelectedWorkspace(): void {
    this.addEditForm.get('admins')?.reset();
    this.addEditForm.get('users')?.reset();
    this.setDefaultDashboardOptions();

    if (!this.isEditDialog()) {
      this.selectCurrentUser();
    }

    this.getDashboards();
  }

  public getUsers(search?: string): void {
    const workspaceId: number | undefined = this.getSelectedWorkspaceId();

    if (!workspaceId) {
      return;
    }

    this.store.loadUserOptions({
      params: {
        fields: ['name'],
        filters: [{ field: 'isActive', ids: [true] }],
        limit: 500,
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      },
      workspaceId,
    });
  }

  public getAdmins(search?: string): void {
    const workspaceId: number | undefined = this.getSelectedWorkspaceId();

    if (!workspaceId) {
      return;
    }

    this.store.loadAdminOptions({
      params: {
        fields: ['name'],
        filters: [{ field: 'isActive', ids: [true] }],
        limit: 500,
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      },
      workspaceId,
    });
  }

  public getTeams(search?: string): void {
    this.store.loadTeamOptions({
      fields: ['name'],
      filters: [{ field: 'isActive', ids: [true] }],
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public getWorkspaces(search?: string): void {
    this.store.loadWorkspaces({
      fields: ['name'],
      sort: [{ active: 'name', direction: 'asc' }],
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public onClickedItem(id: number): void {
    this.selectedDatatableItems.set([id]);
  }

  public togglePin(item: ISlideshowCard): void {
    const isPinned = !item.isPinned;

    this.store.patchOneItem({ changes: { isPinned }, id: item.id });

    this.subscriptions.push(
      this.userPinService.toggle('slideshow', item.id).subscribe({
        error: () => this.store.patchOneItem({ changes: { isPinned: !isPinned }, id: item.id }),
        next: () => this.getItems(this.search),
      }),
    );
  }

  public resetSearch(): void {
    this.search = undefined;
    this.searchControl.setValue('', { emitEvent: false });
    this.getItems();
  }

  public override initializeDelete(): void {
    const [selectedRow] = this.selectedRows();

    if (!selectedRow) {
      return;
    }

    const content = this.translate.instant('system.delete.confirmationSingleContent', {
      context: selectedRow[this.context.nameProperty],
    });

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content,
        submitButtonText: this.translate.instant('button.delete'),
        title: this.translate.instant('system.delete.confirmationTitle', {
          context: this.translate.instant(this.context.singleContext),
        }),
      },
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.datatableStore.deleteOne(selectedRow.id);
        }
      }),
    );
  }

  private getItems(search?: string): void {
    const filters: IGenericCrudRequestConstructionParameterFilters[] = [];

    this.store.loadItems({
      fields: [
        'name',
        'slideDurationSeconds',
        'workspaceId',
        'isPublished',
        'items',
        'canEditUserIds',
        'canViewUserIds',
        'canViewTeamIds',
        'createdAt',
        'autoLockDurationSeconds',
        'isAutoLockConfigured',
      ],
      filters,
      join: ['workspace||name', 'slideshowTeamAssignments||teamId', 'slideshowUserAssignments||userId,isAdmin'],
      limit: 1000,
      page: 1,
      sort: CARD_SORT_REQUEST_SORTS[this.sortOption()],
      ...(search
        ? {
            search: {
              searchedFields: ['name'],
              searchText: search,
            },
          }
        : {}),
    });
  }

  private initializeAddEditDialog(item?: ISlideshow): void {
    this.store.resetDashboards();
    this.isDashboardFormInvalid = false;
    this.isExtendedAccessPanel = false;
    this.isExtendedScreenLockPanel = false;
    this.getUsers();
    this.getAdmins();
    this.getTeams();
    this.getWorkspaces();
    this.getDashboards();

    if (!item) {
      this.store
        .select((state: ISlideshowState) => state.workspacesLoading)
        .pipe(
          filter((value: ELoadStatus) => value === ELoadStatus.success),
          take(1),
        )
        .subscribe(() => {
          const workspaces = this.store.workspaces();
          const oneItem: IDropdownOption | undefined = workspaces.length === 1 ? workspaces[0] : undefined;

          if (oneItem) {
            this.selectCurrentUser();
            this.addEditForm.get('workspace')!.setValue([oneItem]);
            this.getDashboards();
          }
        });

      return;
    }
  }

  private formatOnePayload(formData: TFormSlideshowComponent, items: ISlideshowItem[]): TAddSlideshow {
    return {
      ...ScreenLockConfigurationComponent.toPayload(formData.autoLockDuration, formData.autoLockPin),
      canEditUserIds: formData.admins?.length ? formData.admins.map((admin: IDropdownOption) => admin.id) : [],
      canViewTeamIds: formData.teams?.length ? formData.teams.map((team: IDropdownOption) => team.id) : [],
      canViewUserIds: formData.users?.length ? formData.users.map((user: IDropdownOption) => user.id) : [],
      isPublished: formData.isPublished,
      items,
      name: FormUtilitiesService.trimIfString(formData.name),
      slideDurationSeconds: Number(formData.slideDurationSeconds),
      workspaceId: get(formData.workspace, '0.id'),
    };
  }

  private selectCurrentUser(): void {
    const currentUser: ICurrentUser = UserEffects.getUser();

    this.addEditForm.get('admins')!.setValue([
      {
        id: currentUser.id,
        name: currentUser.name,
      },
    ]);
  }

  public addFieldOption(value: IDropdownOption[] = []): void {
    const dashboardOptionId: string = crypto.randomUUID();
    this.dashboardOptionOrders.update((dashboardOptionOrders: string[]) => [
      ...dashboardOptionOrders,
      dashboardOptionId,
    ]);
    this.dashboardOptions.update((dashboardOptions: IExtendedDashboardOptionForm[]) => [
      ...dashboardOptions,
      {
        id: dashboardOptionId,
        name: this.getFieldOptionFormControl(value),
        type: 'dashboard',
      } as IExtendedDashboardOptionForm,
    ]);
  }

  public addUrlOption(value = ''): void {
    const optionId: string = crypto.randomUUID();
    this.dashboardOptionOrders.update((orders) => [...orders, optionId]);
    this.dashboardOptions.update((options: IExtendedDashboardOptionForm[]) => [
      ...options,
      {
        id: optionId,
        name: new FormControl(value, [Validators.required, IsUrlValidator()]),
        type: 'url',
      } as unknown as IExtendedDashboardOptionForm,
    ]);
  }

  public deleteDashboardOption(optionId: string): void {
    this.dashboardOptionOrders.set(this.dashboardOptionOrders().filter((id: string) => id !== optionId));
    this.dashboardOptions.set(
      this.dashboardOptions().filter(
        (dashboardOption: IExtendedDashboardOptionForm) => dashboardOption.id !== optionId,
      ),
    );
  }

  private getFieldOptionFormControl(value: IDropdownOption[] = []): FormControl<IDropdownOption[]> {
    return new FormControl(value, [Validators.required]) as FormControl<IDropdownOption[]>;
  }

  public getDashboards(search?: string): void {
    const workspaceId: number | undefined = this.getSelectedWorkspaceId();

    if (!workspaceId) {
      return;
    }

    this.store.loadDashboards({
      fields: ['name'],
      filters: [{ field: 'workspaceId', ids: [workspaceId], operator: '$in' }],
      limit: 500,
      sort: [{ active: 'name', direction: 'asc' }],
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  private getOrderedDashboardItems(): ISlideshowItem[] {
    return SlideshowsComponent.orderDashboardItems(this.dashboardOptions(), this.dashboardOptionOrders());
  }

  private setDefaultDashboardOptions(): void {
    this.dashboardOptions.set([
      {
        id: crypto.randomUUID(),
        name: this.getFieldOptionFormControl(),
        type: 'dashboard',
      } as unknown as IExtendedDashboardOptionForm,
      {
        id: crypto.randomUUID(),
        name: this.getFieldOptionFormControl(),
        type: 'dashboard',
      } as unknown as IExtendedDashboardOptionForm,
    ]);
  }

  private getSelectedWorkspaceId(): number | undefined {
    return get(this.addEditForm, 'value.workspace.0.id');
  }

  public validateDashboardForm(): boolean {
    const orderedItems = this.dashboardOptions();

    this.isDashboardFormInvalid = SlideshowsComponent.isSlideListInvalid(orderedItems);

    if (this.isDashboardFormInvalid) {
      orderedItems.forEach((item) => {
        item.name.markAsTouched();
        item.name.updateValueAndValidity();
      });
    }

    return this.isDashboardFormInvalid;
  }
}
